import React, { ReactNode, FormEvent } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import {
  useFormValidation,
  FieldValidation,
  ValidationResult,
} from "../../hooks/useFormValidation";
import { FormContext, FormDataType } from "./FormContext";
import FormFieldErrors from "./FormFieldErrors";

interface FormProps {
  children: ReactNode;
  validationRules: FieldValidation;
  onSubmit: (
    formData: FormDataType,
    validationResult: ValidationResult
  ) => void;
  showErrors?: boolean;
  showFieldErrors?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  initialData?: FormDataType;
  externalErrors?: { [fieldName: string]: string[] | string };
  formRef?: React.RefObject<HTMLFormElement>;
}

const Form: React.FC<FormProps> = ({
  children,
  validationRules,
  onSubmit,
  // showErrors = true,
  showFieldErrors = true,
  className,
  sx,
  initialData,
  externalErrors,
  formRef,
}) => {
  const {
    formData,
    validationResult,
    updateField,
    touchField,
    resetForm,
    submitForm,
    isSubmitted,
    validateForm,
  } = useFormValidation(validationRules, initialData, externalErrors);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const immediateResult = validateForm();

    submitForm();

    const hasClientErrors =
      !immediateResult?.isValid ||
      (Array.isArray(immediateResult?.errorMessages) &&
        immediateResult.errorMessages.length > 0);
    if (hasClientErrors) {
      return;
    }

    onSubmit(formData, immediateResult);
  };

  const formContextValue = {
    formData,
    validationResult,
    updateField,
    touchField,
    resetForm,
    isSubmitted,
  };

  return (
    <FormContext.Provider value={formContextValue}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        className={className}
        sx={sx}
        noValidate
        ref={formRef}
      >
        {children}
        {showFieldErrors && <FormFieldErrors />}
      </Box>
    </FormContext.Provider>
  );
};

export default Form;
